/**
 * 🌐 IPFS Document Status Component
 * 
 * Shows the current IPFS CID and status of the shared Yjs document
 */

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Cloud, Download, Upload, Copy, ExternalLink, RefreshCw } from 'lucide-react'
import { useArchiveStatus, useIPFSDocument } from '../store/PublicArchiveManager.jsx'
import { Button } from './ui'

export default function IPFSDocumentStatus({ className = "" }) {
  const archiveStatus = useArchiveStatus()
  const { saveDocumentToIPFS, getIPFSStatus } = useIPFSDocument()
  const [isLoading, setIsLoading] = useState(false)
  const [lastAction, setLastAction] = useState('')
  const [showDetails, setShowDetails] = useState(false)

  const { ipfs, documentCID, isReady } = archiveStatus

  // Helper to copy CID to clipboard
  const copyCID = async (cid) => {
    if (!cid) return
    
    try {
      await navigator.clipboard.writeText(cid)
      setLastAction(`Copied CID: ${cid.slice(0, 12)}...`)
      setTimeout(() => setLastAction(''), 3000)
    } catch (error) {
      console.error('Failed to copy CID:', error)
    }
  }

  // Helper to open IPFS gateway
  const openInGateway = (cid) => {
    if (!cid) return
    const gatewayUrl = `https://gateway.pinata.cloud/ipfs/${cid}`
    window.open(gatewayUrl, '_blank')
  }

  // Force save document to IPFS
  const handleForceSave = async () => {
    if (!isReady) return
    
    setIsLoading(true)
    try {
      const result = await saveDocumentToIPFS()
      setLastAction(`Saved to IPFS: ${result.cid.slice(0, 12)}...`)
      setTimeout(() => setLastAction(''), 5000)
    } catch (error) {
      console.error('Force save failed:', error)
      setLastAction(`Save failed: ${error.message}`)
      setTimeout(() => setLastAction(''), 5000)
    } finally {
      setIsLoading(false)
    }
  }

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never'
    const date = new Date(timestamp)
    return date.toLocaleTimeString()
  }

  // Format CID for display
  const formatCID = (cid) => {
    if (!cid) return 'No CID'
    return `${cid.slice(0, 8)}...${cid.slice(-8)}`
  }

  // Status indicator color
  const getStatusColor = () => {
    if (!isReady) return 'bg-gray-500'
    if (ipfs?.isConnected && documentCID) return 'bg-green-500'
    if (ipfs?.isConnected) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  return (
    <motion.div 
      className={`bg-white/10 backdrop-blur-lg rounded-lg border border-white/20 p-4 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${getStatusColor()}`} />
          <h3 className="font-semibold text-white">IPFS Document Status</h3>
        </div>
        <Button
          onClick={() => setShowDetails(!showDetails)}
          variant="ghost"
          size="sm"
          className="text-white/70 hover:text-white"
        >
          {showDetails ? 'Hide' : 'Details'}
        </Button>
      </div>

      {/* Current CID */}
      {documentCID && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Cloud className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-white/70">Current Document CID:</span>
          </div>
          <div className="flex items-center gap-2 bg-black/20 rounded px-3 py-2">
            <code className="text-sm text-green-400 font-mono flex-1">
              {formatCID(documentCID)}
            </code>
            <Button
              onClick={() => copyCID(documentCID)}
              variant="ghost"
              size="sm"
              className="p-1 h-auto text-white/70 hover:text-white"
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => openInGateway(documentCID)}
              variant="ghost"
              size="sm"
              className="p-1 h-auto text-white/70 hover:text-white"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mb-4">
        <Button
          onClick={handleForceSave}
          disabled={!isReady || isLoading}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isLoading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {isLoading ? 'Saving...' : 'Force Save'}
        </Button>
      </div>

      {/* Last Action */}
      {lastAction && (
        <div className="mb-4 text-sm text-green-400 bg-green-400/10 rounded px-2 py-1">
          {lastAction}
        </div>
      )}

      {/* Details */}
      {showDetails && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="space-y-3 text-sm text-white/70"
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-medium text-white/90 mb-1">IPFS Status</div>
              <div className={ipfs?.isConnected ? 'text-green-400' : 'text-red-400'}>
                {ipfs?.isConnected ? 'Connected' : 'Disconnected'}
              </div>
            </div>
            <div>
              <div className="font-medium text-white/90 mb-1">Last Save</div>
              <div>{formatTimestamp(ipfs?.lastSaveTime)}</div>
            </div>
            <div>
              <div className="font-medium text-white/90 mb-1">Document ID</div>
              <div className="font-mono text-xs">{ipfs?.documentId}</div>
            </div>
            <div>
              <div className="font-medium text-white/90 mb-1">Auto Save</div>
              <div className={ipfs?.autoSave ? 'text-green-400' : 'text-yellow-400'}>
                {ipfs?.autoSave ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          </div>

          {/* Full CID */}
          {documentCID && (
            <div>
              <div className="font-medium text-white/90 mb-1">Full CID</div>
              <div className="bg-black/20 rounded p-2 break-all font-mono text-xs">
                {documentCID}
              </div>
            </div>
          )}

          {/* IPFS Gateways */}
          {documentCID && (
            <div>
              <div className="font-medium text-white/90 mb-2">IPFS Gateways</div>
              <div className="space-y-1">
                {[
                  'https://gateway.pinata.cloud/ipfs/',
                  'https://cloudflare-ipfs.com/ipfs/',
                  'https://gateway.ipfs.io/ipfs/',
                  'https://dweb.link/ipfs/'
                ].map((gateway, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <button
                      onClick={() => window.open(`${gateway}${documentCID}`, '_blank')}
                      className="text-blue-400 hover:text-blue-300 text-xs font-mono flex-1 text-left"
                    >
                      {gateway}{formatCID(documentCID)}
                    </button>
                    <ExternalLink className="w-3 h-3 text-white/40" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  )
}
